import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, CheckCircle2 } from "lucide-react";
import { services, whyChoose, stats } from "../mock";

const Home = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative bg-gradient-to-b from-teal-50/70 to-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(13,148,136,0.08),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="reveal">
              <span className="inline-block bg-teal-100 text-teal-800 text-xs font-bold tracking-[0.12em] px-4 py-2 rounded-full">
                INTERNATIONAL TRADE & DEVELOPMENT
              </span>
              <h1 className="mt-6 text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-slate-900">
                Connecting Markets,
                <br />
                <span className="text-teal-600">Building Partnerships</span>
              </h1>
              <p className="mt-6 text-lg text-slate-500 max-w-md leading-relaxed">
                Your trusted consulting partner for international trade and
                development across US, India, and Latin America.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-7 py-3.5 rounded-full transition-all duration-200 hover:shadow-lg hover:shadow-teal-600/25 active:scale-95"
                >
                  Get in Touch <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-800 font-semibold px-7 py-3.5 rounded-full transition-all duration-200 hover:border-teal-300 hover:text-teal-700 active:scale-95"
                >
                  Our Services
                </Link>
              </div>
            </div>

            <div className="flex justify-center lg:justify-end reveal">
              <div className="relative w-72 h-72 lg:w-96 lg:h-96 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-teal-100/70 float-anim" />
                <div className="absolute inset-8 rounded-full bg-teal-50" />
                <Globe
                  className="relative w-40 h-40 lg:w-52 lg:h-52 text-teal-500"
                  strokeWidth={1.2}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl font-extrabold text-teal-600">
                  {s.value}
                </div>
                <div className="mt-1 text-sm font-medium text-slate-500">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-4xl font-extrabold tracking-tight text-slate-900">
              Our Core Services
            </h2>
            <p className="mt-4 text-lg text-slate-500">
              Comprehensive consulting solutions to help your business thrive in
              international markets.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <div
                  key={service.id}
                  className="group bg-white border border-slate-100 rounded-2xl p-7 transition-all duration-300 hover:shadow-xl hover:shadow-slate-200/60 hover:-translate-y-1"
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-teal-50 text-teal-600 transition-colors group-hover:bg-teal-600 group-hover:text-white">
                    <Icon className="w-6 h-6" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-slate-900">
                    {service.title}
                  </h3>
                  <p className="mt-3 text-[15px] text-slate-500 leading-relaxed">
                    {service.description}
                  </p>
                  <Link
                    to="/services"
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:gap-2.5 transition-all"
                  >
                    Learn more <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-center text-4xl font-extrabold tracking-tight text-slate-900">
            Why Choose JLa Consultants LLC?
          </h2>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {whyChoose.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-white rounded-2xl p-7 text-center border border-slate-100 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                >
                  <div className="mx-auto flex items-center justify-center w-14 h-14 rounded-full bg-teal-100 text-teal-600">
                    <Icon className="w-7 h-7" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-slate-900">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-teal-600">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-20 text-center">
          <Globe className="mx-auto w-12 h-12 text-teal-200" strokeWidth={1.6} />
          <h2 className="mt-6 text-4xl font-extrabold text-white tracking-tight">
            Ready to Expand Your Business Globally?
          </h2>
          <p className="mt-4 text-lg text-teal-50/90">
            Let's discuss how we can help you navigate international markets and
            build successful partnerships.
          </p>
          <Link
            to="/contact"
            className="mt-9 inline-flex items-center gap-2 bg-white text-teal-700 font-semibold px-8 py-3.5 rounded-full transition-all duration-200 hover:shadow-xl hover:scale-105 active:scale-95"
          >
            Start the Conversation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
